import {UserTierTypes} from '@eCommerce/frontend-legacy-shared-library';
import {faEnvelope} from '@fortawesome/pro-solid-svg-icons/faEnvelope';
import {faPhone} from '@fortawesome/pro-solid-svg-icons/faPhone';
import {faUser} from '@fortawesome/pro-solid-svg-icons/faUser';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import CAESARS_LAUREL_GOLD from 'assets/svg/caesars_laurel_gold.svg';
import CAESARS_LAUREL_GREY from 'assets/svg/caesars_laurel_grey.svg';
import CAESARS_LAUREL_WHITE from 'assets/svg/caesars_laurel_white.svg';
import {HostInfoTypes} from 'models/HostInfo';
import {supportPhoneNumber} from 'models/supportPhoneNumber';
import {Text, View} from 'native-base';
import {useEffect} from 'react';
import {formatPhoneNumber} from 'utils/utils';

import PanelContainer from 'components/PanelContainer';
import {useIsMD} from 'hooks/useIsMD';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';

import {theme} from '../../theme/';

import {isUserTierDiamond, isUserTierSevenStars} from './userTierHelpers';

export interface VIPHostInfoHotlineProps {
  tier: UserTierTypes | undefined;
  hostInfo: HostInfoTypes;
  isLoading: boolean;
}

const VIPHostInfoHotline: React.FC<VIPHostInfoHotlineProps> = (props) => {
  const {tier, hostInfo, isLoading} = props;
  const {name, phone, email, imageUrl} = hostInfo;
  const {caesarRewards, white, ghostWhite, lightGray} = theme.colors;

  const isDiamond = isUserTierDiamond(tier);
  const isSevenStars = isUserTierSevenStars(tier);
  const isSevenOrDiamond = isSevenStars || isDiamond;
  const isMD = useIsMD();
  const {trackAdobeEvent} = useTrackEvent();

  useEffect(() => {
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {
        name: 'mfa host information',
      },
    });
  });

  const displayPhone = phone || (!phone && !email);

  const getBodyText = () => {
    if (isSevenStars) {
      return 'As a Seven Stars member, we have made it easy to secure your account safely. Please continue via the Seven Stars Hotline.';
    } else if (isDiamond) {
      return 'As a Diamond member, we have made it easy to secure your account safely. Please continue via the Diamond Hotline.';
    } else {
      return 'As a hosted player, please set up your extra layer of security by contacting your host.';
    }
  };

  const getCTAs = () => {
    if (!phone && !email) {
      return {primary: 'Contact Us', secondary: undefined};
    } else if (phone && !email) {
      return {primary: 'Call Host', secondary: undefined};
    } else if (!phone && email) {
      return {primary: 'Email Host', secondary: undefined};
    }
    return {primary: 'Call Host', secondary: 'Email Host'};
  };

  const callHostPressed = () => {
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {name: 'mfa call host'},
    });
    window.location.href = `tel:${phone || supportPhoneNumber}`;
  };

  const emailHostPressed = () => {
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {name: 'mfa email host'},
    });
    !phone && !email ? undefined : (window.location.href = `mailto:${email}`);
  };

  return (
    <PanelContainer
      title="SECURE YOUR ACCOUNT"
      bodyText={getBodyText()}
      primaryTitle={getCTAs().primary}
      secondaryTitle={getCTAs().secondary}
      onPrimaryPress={!phone && email ? emailHostPressed : callHostPressed}
      onSecondaryPress={emailHostPressed}
      isLoading={isLoading}
    >
      <View
        flexDirection={isMD ? 'row' : 'column'}
        px={isMD ? '24px' : '16px'}
        pt="24px"
        pb={isSevenOrDiamond ? '32px' : '24px'}
        backgroundColor={ghostWhite}
        borderRadius="8px"
        alignItems="center"
        justifyContent={isMD ? undefined : 'center'}
      >
        {isSevenOrDiamond ? (
          <View
            width="64px"
            height="64px"
            backgroundColor={isSevenStars ? caesarRewards : white}
            mr={isMD ? '32px' : undefined}
            justifyContent="center"
            alignItems="center"
            borderRadius="50px"
            borderWidth={isDiamond ? '1px' : undefined}
            borderColor={isDiamond ? lightGray : undefined}
            style={{
              zIndex: 1,
            }}
          >
            <img
              src={isSevenStars ? CAESARS_LAUREL_WHITE : CAESARS_LAUREL_GREY}
              width={48}
              height={48}
              style={{
                zIndex: 2,
                position: 'absolute',
              }}
              alt="host"
            />
          </View>
        ) : imageUrl ? (
          <img
            width={100}
            height={100}
            style={{borderRadius: '100px'}}
            src={imageUrl}
            alt="host"
          />
        ) : (
          <View
            height="90px"
            width="90px"
            borderRadius="50px"
            backgroundColor={lightGray}
            justifyContent="center"
            alignItems="center"
            mr={isMD ? '48px' : undefined}
          >
            <FontAwesomeIcon
              icon={faUser}
              color={caesarRewards}
              style={{
                position: 'relative',
                fontSize: 56,
              }}
            />
          </View>
        )}
        <View justifyContent={isMD ? 'left' : 'center'}>
          <Text
            fontSize="18px"
            pt={isMD ? undefined : '16px'}
            pb="16px"
            lineHeight="22px"
            color="#343840"
            fontFamily="heading"
            fontWeight="bold"
            textAlign={isMD ? undefined : 'center'}
            textTransform="uppercase"
          >
            {name ? name : 'Contact Support'}
          </Text>
          <View alignItems={isMD ? undefined : 'center'}>
            {displayPhone && (
              <Text
                fontFamily="body"
                fontSize="14px"
                color="#343840"
                lineHeight="24px"
              >
                <FontAwesomeIcon
                  icon={faPhone}
                  color={caesarRewards}
                  style={{
                    position: 'relative',
                    fontSize: 14,
                    marginRight: '8px',
                  }}
                />
                {phone
                  ? formatPhoneNumber(phone)
                  : !email && !phone && supportPhoneNumber}
              </Text>
            )}
            {email && (
              <Text
                pt={displayPhone ? '8px' : undefined}
                fontFamily="body"
                fontSize="14px"
                color="#343840"
                lineHeight="24px"
              >
                <FontAwesomeIcon
                  icon={faEnvelope}
                  color={caesarRewards}
                  style={{
                    position: 'relative',
                    fontSize: 14,
                    lineHeight: '24px',
                    marginRight: '8px',
                  }}
                />
                {email}
              </Text>
            )}
          </View>
        </View>
      </View>
    </PanelContainer>
  );
};

export default VIPHostInfoHotline;
